import React,{createContext,useContext,useEffect,useMemo,useState}from"react";
import{Navigate,useLocation}from"react-router-dom";

const AuthContext=createContext(null);

const allowedAdminRoles=["super_admin","admin","staff"];

const sessionExpiredMessage="Error de conexión. Inicia sesión nuevamente.";

const tokenErrorMessages=[
  "token dado no es valido",
  "token dado no es válido",
  "no es valido para ningun tipo de token",
  "no es válido para ningún tipo de token",
  "token_not_valid",
  "token given not valid",
  "not valid for any token type",
  "invalid token",
  "user_not_found",
];

const isTokenError=value=>{
  const text=String(value||"").toLowerCase();
  return tokenErrorMessages.some(message=>text.includes(message));
};

const safeJson=async response=>{
  try{
    return await response.json();
  }catch{
    return null;
  }
};

const requiresPasswordChangeValue=user=>{
  return(
    user?.requires_password_change===true||
    user?.requiere_cambio_clave===true||
    user?.must_change_password===true||
    user?.change_password_required===true||
    user?.temporary_password===true
  );
};

const normalizePermissions=value=>{
  if(!Array.isArray(value)){
    return[];
  }

  return value.map(item=>{
    if(typeof item==="string"){
      return item.trim();
    }

    if(item?.code){
      return String(item.code).trim();
    }

    if(item?.permission?.code){
      return String(item.permission.code).trim();
    }

    if(item?.permission_code){
      return String(item.permission_code).trim();
    }

    return"";
  }).filter(Boolean);
};

export const AuthProvider=({children})=>{
  const[accessToken,setAccessToken]=useState(()=>localStorage.getItem("accessToken"));
  const[refreshToken,setRefreshToken]=useState(()=>localStorage.getItem("refreshToken"));
  const[sessionMessage,setSessionMessage]=useState("");

  const[user,setUser]=useState(()=>{
    const storedUser=localStorage.getItem("user");

    if(!storedUser){
      return null;
    }

    try{
      const parsed=JSON.parse(storedUser);

      return{
        ...parsed,
        permissions:normalizePermissions(parsed?.permissions),
      };
    }catch{
      localStorage.removeItem("user");
      return null;
    }
  });

  const[authChecking,setAuthChecking]=useState(true);

  const isAuthenticated=Boolean(accessToken&&user);

  const clearAuthStorage=()=>{
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  };

  const logout=(message="")=>{
    clearAuthStorage();
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setSessionMessage(message);
  };

  const forceSessionExpired=()=>{
    logout(sessionExpiredMessage);
  };

  const loadPermissions=async token=>{
    try{
      const response=await fetch("/api/access-control/my-permissions/",{
        method:"GET",
        headers:{
          Authorization:`Bearer ${token}`,
        },
      });

      const data=await safeJson(response);

      if(!response.ok){
        if(response.status===401||isTokenError(JSON.stringify(data))){
          forceSessionExpired();
        }

        return[];
      }

      return normalizePermissions(data?.permissions);
    }catch{
      return[];
    }
  };

  const login=async({access,refresh,user:userData,skipPermissions=false})=>{
    if(!access||!userData){
      return null;
    }

    localStorage.setItem("accessToken",access);
    setAccessToken(access);

    if(refresh){
      localStorage.setItem("refreshToken",refresh);
      setRefreshToken(refresh);
    }

    setSessionMessage("");

    const baseUser={
      ...userData,
      permissions:normalizePermissions(userData.permissions),
    };

    localStorage.setItem("user",JSON.stringify(baseUser));
    setUser(baseUser);

    if(skipPermissions||requiresPasswordChangeValue(baseUser)){
      return baseUser;
    }

    const permissions=await loadPermissions(access);

    const finalUser={
      ...baseUser,
      permissions,
    };

    localStorage.setItem("user",JSON.stringify(finalUser));
    setUser(finalUser);

    return finalUser;
  };

  const validateSession=async()=>{
    const token=localStorage.getItem("accessToken");
    const refresh=localStorage.getItem("refreshToken");

    if(!token){
      setAuthChecking(false);
      return null;
    }

    try{
      const response=await fetch("/api/auth/me/",{
        method:"GET",
        headers:{
          Authorization:`Bearer ${token}`,
        },
      });

      const data=await safeJson(response);

      if(!response.ok){
        if(response.status===401||isTokenError(JSON.stringify(data))){
          logout(sessionExpiredMessage);
        }else{
          logout("Error de conexión. Inicia sesión nuevamente.");
        }

        setAuthChecking(false);
        return null;
      }

      const finalUser=await login({
        access:token,
        refresh,
        user:data,
        skipPermissions:requiresPasswordChangeValue(data),
      });

      setAuthChecking(false);

      return finalUser;
    }catch{
      logout("Error de conexión. Inicia sesión nuevamente.");
      setAuthChecking(false);
      return null;
    }
  };

  const refreshUserPermissions=async()=>{
    const token=localStorage.getItem("accessToken");

    if(!token||!user){
      return[];
    }

    const permissions=await loadPermissions(token);

    const updatedUser={
      ...user,
      permissions,
    };

    localStorage.setItem("user",JSON.stringify(updatedUser));
    setUser(updatedUser);

    return permissions;
  };

  useEffect(()=>{
    validateSession();
  },[]);

  useEffect(()=>{
    const handleSessionExpired=()=>{
      forceSessionExpired();
    };

    window.addEventListener("session-expired",handleSessionExpired);

    return()=>{
      window.removeEventListener("session-expired",handleSessionExpired);
    };
  },[]);

  const value=useMemo(()=>({
    accessToken,
    refreshToken,
    user,
    isAuthenticated,
    authChecking,
    sessionMessage,
    login,
    logout,
    validateSession,
    refreshUserPermissions,
    forceSessionExpired,
  }),[
    accessToken,
    refreshToken,
    user,
    isAuthenticated,
    authChecking,
    sessionMessage,
  ]);

  return(
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth=()=>useContext(AuthContext);

export const ProtectedRoute=({children,allowedRoles})=>{
  const{isAuthenticated,authChecking,user,sessionMessage}=useAuth();
  const location=useLocation();

  if(authChecking){
    return null;
  }

  if(!isAuthenticated){
    return(
      <Navigate
        to="/"
        replace
        state={{
          from:location,
          message:sessionMessage||sessionExpiredMessage,
        }}
      />
    );
  }

  if(requiresPasswordChangeValue(user)&&location.pathname!=="/actualizar-password"){
    return(
      <Navigate
        to="/actualizar-password"
        replace
        state={{
          email:user.email,
        }}
      />
    );
  }

  const validRoles=allowedRoles||allowedAdminRoles;

  if(!user?.role||!validRoles.includes(user.role)){
    return(
      <Navigate
        to="/"
        replace
        state={{
          message:"No tienes permisos para ingresar.",
        }}
      />
    );
  }

  return children;
};